import React from "react";
import AppButton from "../AppButton";
import "./style.css";

type AppPaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  buttonStyle?: React.CSSProperties;
  activeStyle?: React.CSSProperties;
  buttonClassName?: string;
  activeClassName?: string;
  maxVisiblePages?: number;
};

const AppPagination: React.FC<AppPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  buttonStyle,
  activeStyle,
  buttonClassName,
  activeClassName,
  maxVisiblePages = 10,
}) => {
  const visiblePages = getVisiblePages(currentPage, totalPages, maxVisiblePages);

  function getVisiblePages(current: number, total: number, maxVisible: number): number[] {
    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: number[] = [];
    
    // Se maxVisible for 1, mostrar apenas a página atual
    if (maxVisible === 1) {
      return [current];
    }

    // Se maxVisible for 2, mostrar apenas primeira e última página
    if (maxVisible === 2) {
      if (current === 1 || current === total) {
        return [current];
      }
      return [1, -1, total]; // primeira ... última
    }

    const firstPage = 1;
    const lastPage = total;
    
    // Calcular quantas páginas mostrar no meio (excluindo primeira e última)
    const middlePages = Math.max(0, maxVisible - 2);
    const halfWindow = Math.floor(middlePages / 2);
    
    let start = Math.max(current - halfWindow, 2);
    let end = Math.min(current + halfWindow, total - 1);

    // Ajustar se estiver perto do início
    if (current - halfWindow < 2) {
      end = Math.min(2 + middlePages - 1, total - 1);
    }
    
    // Ajustar se estiver perto do fim
    if (current + halfWindow > total - 1) {
      start = Math.max(total - middlePages, 2);
    }

    pages.push(firstPage);
    if (start > 2) {
      pages.push(-1); // Ellipsis
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < total - 1) {
      pages.push(-2); // Ellipsis
    }
    if (total > 1) {
      pages.push(lastPage);
    }
    return pages;
  }

  const handleClick = (page: number) => {
    if (page !== currentPage && page > 0 && page <= totalPages) {
      onPageChange(page);
    }
  };

  return (
    <div className="app-pagination">
      <AppButton
        label="Anterior"
        onClick={() => handleClick(currentPage - 1)}
        disabled={currentPage === 1}
        className={`app-pagination-button back ${buttonClassName}`}
        style={buttonStyle}
      />

      {visiblePages.map((page, index) =>
        page > 0 ? (
          <AppButton
            label={String(page)}
            key={index}
            onClick={() => handleClick(page)}
            className={`app-pagination-button page ${currentPage === page ? `active ${activeClassName}` : `${buttonClassName}`}`}
            style={{ ...(currentPage === page ? activeStyle : { ...buttonStyle }) }}
          />
        ) : (
          <span key={index} className="app-pagination-dots">
            ...
          </span>
        )
      )}

      <AppButton
        label="Próximo"
        onClick={() => handleClick(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`app-pagination-button next ${buttonClassName}`}
        style={buttonStyle}
      />
    </div>
  );
};

export default AppPagination;
